/* Direction-aware scrolling — the single source of truth for every carousel.
   See rtl-spec.md (Phase 2).

   Why this exists: `scrollLeft` in an RTL container is not portable. Per CSSOM
   it is 0 at the inline-start (the RIGHT edge in RTL) and goes NEGATIVE toward
   the end, but legacy engines used two other conventions. Meanwhile `offsetLeft`
   is ALWAYS physical and LTR-origin, so the very common
   `track.scrollTo({ left: el.offsetLeft })` mixes two coordinate systems and
   silently breaks in RTL. Every carousel here had some variant of that bug, so
   the direction logic lives in one place rather than in ~9 call sites.

   Two kinds of primitive:
   - Rect-based (deltaToElement/scrollToElement/nearestIndex/page). These work in
     PHYSICAL pixels measured live off getBoundingClientRect, so they need no
     RTL detection at all and are correct under every convention. Prefer these.
   - Offset-based (startOffset/setStartOffset/atStart/atEnd). These need a real
     scroll POSITION, so they normalise via a one-time feature test. Use only
     when a rect-based answer will not do — chiefly end-of-track detection. */
(function () {
  if (window.DieselScroll) return;

  /* ---- RTL scrollLeft convention, feature-detected once ----
     'negative' : spec / modern engines. start = 0, end = -(scrollWidth-clientWidth)
     'default'  : legacy WebKit.        start = max, end = 0
     'reverse'  : legacy IE/Edge.       start = 0, end = +max  */
  var _rtlType = null;
  function rtlScrollType() {
    if (_rtlType) return _rtlType;
    /* This file loads in <head> so the carousels can call it during parse;
       document.body may not exist yet. Answer without caching until it does. */
    if (!document.body) return 'negative';
    var probe = document.createElement('div');
    probe.setAttribute('dir', 'rtl');
    probe.style.cssText =
      'position:absolute;top:-9999px;left:-9999px;width:100px;height:50px;overflow:scroll;visibility:hidden;';
    probe.innerHTML = '<div style="width:300px;height:10px"></div>';
    document.body.appendChild(probe);
    var type;
    if (probe.scrollLeft > 0) {
      type = 'default';
    } else {
      probe.scrollLeft = 1;
      type = probe.scrollLeft === 0 ? 'negative' : 'reverse';
    }
    document.body.removeChild(probe);
    _rtlType = type;
    return type;
  }

  function isRTL(el) {
    var node = el && el.nodeType === 1 ? el : document.documentElement;
    return window.getComputedStyle(node).direction === 'rtl';
  }

  function maxScroll(track) {
    return Math.max(0, track.scrollWidth - track.clientWidth);
  }

  /* Distance scrolled from the inline-start edge: always 0..maxScroll, both
     directions. This is the value carousels actually mean when they say
     "how far along are we". */
  function startOffset(track) {
    var sl = track.scrollLeft;
    if (!isRTL(track)) return sl;
    var t = rtlScrollType();
    if (t === 'negative') return -sl;
    if (t === 'default') return maxScroll(track) - sl;
    return sl; /* 'reverse' */
  }

  function setStartOffset(track, value, behavior) {
    var target = value;
    if (isRTL(track)) {
      var t = rtlScrollType();
      if (t === 'negative') target = -value;
      else if (t === 'default') target = maxScroll(track) - value;
    }
    track.scrollTo({ left: target, behavior: behavior || 'auto' });
  }

  function atStart(track, tol) {
    return startOffset(track) <= (tol == null ? 2 : tol);
  }

  /* The one every carousel got wrong: `scrollLeft + clientWidth >= scrollWidth`
     is guaranteed FALSE in RTL (scrollLeft is <= 0), so autoplay never wrapped. */
  function atEnd(track, tol) {
    return startOffset(track) >= maxScroll(track) - (tol == null ? 2 : tol);
  }

  function scrollToStart(track, behavior) {
    setStartOffset(track, 0, behavior);
  }

  /* PHYSICAL delta in px (+ve moves the viewport right). Rect-derived, so it is
     correct in both directions with no convention detection.
     align: 'start' (inline-start edges flush) | 'center'. */
  function deltaToElement(track, el, align) {
    var tr = track.getBoundingClientRect();
    var er = el.getBoundingClientRect();
    if (align === 'center') {
      return er.left + er.width / 2 - (tr.left + tr.width / 2);
    }
    return isRTL(track) ? er.right - tr.right : er.left - tr.left;
  }

  function scrollToElement(track, el, opts) {
    opts = opts || {};
    var delta = deltaToElement(track, el, opts.align || 'start');
    if (opts.instant || opts.behavior === 'auto') {
      /* Containers set scroll-behavior:smooth in CSS, which animates even a
         behavior:'auto' scroll — override inline to force a true jump. */
      var prev = track.style.scrollBehavior;
      track.style.scrollBehavior = 'auto';
      track.scrollLeft += delta;
      track.style.scrollBehavior = prev;
    } else {
      track.scrollBy({ left: delta, behavior: opts.behavior || 'smooth' });
    }
  }

  /* Logical paging. dir: +1 = next in reading order, -1 = previous.
     In RTL "next" moves the viewport LEFT, so the physical sign flips. */
  function page(track, dir, amount, behavior) {
    var physical = (isRTL(track) ? -dir : dir) * amount;
    track.scrollBy({ left: physical, behavior: behavior || 'smooth' });
  }

  /* DOM index of the item nearest the track's centre (or inline-start edge).
     Replaces offsetLeft-vs-scrollLeft comparisons. */
  function nearestIndex(track, selector, align) {
    var items = track.querySelectorAll(selector);
    if (!items.length) return -1;
    var rtl = isRTL(track);
    var tr = track.getBoundingClientRect();
    var ref = align === 'start' ? (rtl ? tr.right : tr.left) : tr.left + tr.width / 2;
    var best = 0;
    var bestDist = Infinity;
    for (var i = 0; i < items.length; i++) {
      var r = items[i].getBoundingClientRect();
      var c = align === 'start' ? (rtl ? r.right : r.left) : r.left + r.width / 2;
      var d = Math.abs(c - ref);
      if (d < bestDist) {
        bestDist = d;
        best = i;
      }
    }
    return best;
  }

  /* Page index for a uniform-width scroll-snap track (the card-gallery dots).
     Uses startOffset so RTL's negative scrollLeft cannot produce a negative
     index that clamps every dot to the first. */
  function indexFromOffset(track, itemWidth, count) {
    var w = itemWidth || track.clientWidth || 1;
    var i = Math.round(startOffset(track) / w);
    if (i < 0) i = 0;
    if (count && i > count - 1) i = count - 1;
    return i;
  }

  /* Arrow keys are physical; carousel movement is logical. In RTL, ArrowRight
     means "previous". Returns +1 next, -1 previous, 0 if not an arrow key. */
  function arrowDir(key, el) {
    if (key !== 'ArrowRight' && key !== 'ArrowLeft') return 0;
    var forward = key === 'ArrowRight';
    if (isRTL(el)) forward = !forward;
    return forward ? 1 : -1;
  }

  window.DieselScroll = {
    isRTL: isRTL,
    maxScroll: maxScroll,
    startOffset: startOffset,
    setStartOffset: setStartOffset,
    atStart: atStart,
    atEnd: atEnd,
    scrollToStart: scrollToStart,
    deltaToElement: deltaToElement,
    scrollToElement: scrollToElement,
    page: page,
    nearestIndex: nearestIndex,
    indexFromOffset: indexFromOffset,
    arrowDir: arrowDir
  };
})();
